import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import { toast, ToastContainer } from "react-toastify";
import AppRouter from "./Routes/AppRouter";
import { ChatSettingsProvider } from "./Context/ChatSettingsContext";
import { ChatBubbleBgContextProvider } from "./Context/ChatBubbleBgContext";
import { ThemeProvider } from "./Context/ThemeContext";
import { getApiCaller, postFormDataApiCaller } from "./Libs/apiCaller";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { logedInUser, logOutUser, setFcmToken } from "./Redux/slices/AuthSlice";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { requestForToken } from "./Components/Notifications/Notification";
import { onMessage } from "firebase/messaging";
import { messaging } from "./Libs/firebaseConfig";
import axios from "axios";

function App() {
  const dispatch = useDispatch();
  const [cookies, , removeCookie] = useCookies(["accessToken"]);
  const navigate = useNavigate();

  // Setup global 401 handler via custom event
  useEffect(() => {
    const handleLogout = (event) => {
      const authPages = ['/sign-in', '/sign-up', '/signin-via-number', '/otp-verify-mobile', '/otp-verify-signup', '/forgot-password'];
      const currentPath = window.location.pathname;

      if (!authPages.some(page => currentPath.includes(page))) {
        // Log out user
        dispatch(logOutUser());
        removeCookie("accessToken", {
          path: "/",
          secure: true,
          httpOnly: false,
          sameSite: "Strict",
        });
        // Clear localStorage
        localStorage.removeItem("userId");
        // Redirect to login
        navigate("/sign-in", { replace: true });
        toast.error(event.detail?.reason || "Your session has expired. Please log in again.");
      }
    };

    window.addEventListener('auth:logout', handleLogout);

    // Cleanup
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [dispatch, navigate, removeCookie]);

  useEffect(() => {
    if (cookies.accessToken) {
      getUserProfile();
    }
  }, []);

  const getUserProfile = async () => {
    const endPoint = "users/get-profile";
    const response = await getApiCaller(endPoint);
    if (response?.status === 200) {
      dispatch(logedInUser(response.data.user));
    } else if (response?.status === 401) {
      dispatch(logOutUser());
      removeCookie("accessToken", {
        path: "/",
        secure: true,
        httpOnly: false,
        sameSite: "Strict",
      }); // Remove authentication token
      navigate("/sign-in");
    } else {
      toast.error(response?.response?.data?.message || response?.message);
    }
  };
  useEffect(() => {
    getTokenAndDispatch();
    onMessage(messaging, (payload) => {
      try {
        // Safely extract title/body from either notification block or data payload
        const notification = payload?.notification || {};
        const data = payload?.data || {};
        const title = notification.title || data.title || "New Message";
        const body = notification.body || data.body || "";

        // Only attempt browser notification if supported and permission is granted
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(title, { body });
        } else {
          // Best-effort: request permission once if it is still in default state
          if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission().then((permission) => {
              if (permission === "granted") {
                new Notification(title, { body });
              }
            });
          }
        }
      } catch (err) {
        // Avoid noisy logs in production; only emit detailed errors in development
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("Error handling foreground FCM message:", err, payload);
        }
      }
    });

    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register("/firebase-messaging-sw.js")
          .then((reg) => {
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.log("SW registered:", reg);
            }
          })
          .catch((err) => {
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.error("SW registration failed:", err);
            }
          });
      });
    }
  }, []);

  const handleUpdateProfile = async (oldToken, newToken) => {
    const formData = new FormData();
    formData.append("oldFcmToken", oldToken);
    formData.append("newFcmToken", newToken);
    const endPoint = "users/update_user";
    await postFormDataApiCaller(endPoint, formData);
  };

  const getTokenAndDispatch = async () => {
    const token = await requestForToken();
    const oldToken = localStorage.getItem("fcmToken");
    if (oldToken !== token) {
      await handleUpdateProfile(oldToken, token);
      localStorage.setItem("fcmToken", token);
    }
    if (token) {
      dispatch(setFcmToken(token));
    }
  };

  return (
    <>
      <style>{`
        /* Glass Toast Styles - Modern Dynamic Island Design */
        .glass-toast {
          background: rgba(24, 24, 27, 0.9) !important;
          backdrop-filter: blur(12px) !important;
          -webkit-backdrop-filter: blur(12px) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 9999px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
          padding: 8px 16px !important;
          min-width: 300px !important;
          max-width: 400px !important;
        }
        
        .glass-toast .Toastify__toast-body {
          padding: 0 !important;
          margin: 0 !important;
        }
        
        /* Remove default Toastify styles */
        .Toastify__toast {
          background: transparent !important;
          box-shadow: none !important;
        }
        
        .Toastify__toast-container--top-center {
          top: 1rem !important;
        }
      `}</style>

      <ThemeProvider>
        <ChatBubbleBgContextProvider>
          <ChatSettingsProvider>
            <AppRouter />
            <ToastContainer
              position="top-center"
              newestOnTop
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
            />
          </ChatSettingsProvider>
        </ChatBubbleBgContextProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
